import { Box, FormControlLabel, Switch } from '@mui/material';
import React from 'react';
import { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Types } from '../../enums/TypeEnums';
import {
  ICountry,
  ICurrency,
  IIndex,
  useCountriesQuery,
  useCreateIndexMutation,
  useCurrenciesQuery,
  useUpdateIndexMutation
} from '../../generated/graphql';
import { sameIndexCodeExists } from '../../utils/clientUtils';
import { canUserEdit } from '../../utils/rightUtils';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import EditButton from '../Common/Buttons/EditButton';
import SaveButton from '../Common/Buttons/SaveButton';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import InputComponent from '../Common/Fields/InputComponent';
import DialogBox from '../Common/Layout/DialogBox';

type IProps = {
  handleClose: (saved: boolean, isNew?: boolean, indexInput?: IIndex) => void;
  indexInput: IIndex | null;
};

export type IErrors = {
  indexName: string | null;
  country: string | null;
  currency: string | null;
  identifier: string | null;
  code: string | null;
};

const defaultErrorsObj: IErrors = {
  indexName: null,
  country: null,
  currency: null,
  identifier: null,
  code: null
};

const errorTexts = {
  indexName: 'Index name is required',
  country: 'Country is required',
  currency: 'Currency is required',
  identifier: 'Index identifier is required',
  code: 'Index code is required',
  existingCode: 'Index code already exists'
};
const IndexForm: React.FunctionComponent<IProps> = ({ handleClose, indexInput }: IProps) => {
  const [editable, setEditable] = useState(indexInput === null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [indexName, setIndexName] = useState(indexInput?.name || null);
  const [indexCountry, setIndexCountry] = useState(indexInput?.country || null);
  const [indexCurrency, setIndexCurrency] = useState(indexInput?.currency || null);
  const [indexIdentifier, setIndexIdentifier] = useState(indexInput?.identifier || null);
  const [indexCode, setIndexCode] = useState(indexInput?.code || null);
  const [indexNetCode, setIndexNetCode] = useState(indexInput?.net_code || null);
  const [isOnline, setIsOnline] = useState(indexInput?.is_online || false);
  const [createIndex, createIndexResult] = useCreateIndexMutation();
  const [updateIndex, updateIndexResult] = useUpdateIndexMutation();
  const indexCountries = useCountriesQuery();
  const indexCurrencies = useCurrenciesQuery();
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Index);

  const handleCancel = () => {
    if (editable && indexInput !== null) {
      setIndexName(indexInput?.name);
      setIndexCountry(indexInput?.country || null);
      setIndexCurrency(indexInput?.currency || null);
      setIndexIdentifier(indexInput?.identifier || null);
      setIndexCode(indexInput?.code || null);
      setIndexNetCode(indexInput?.net_code || null);
      setIsOnline(indexInput?.is_online || false);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      code: null,
      country: null,
      currency: null,
      identifier: null,
      indexName: null
    };
    if (!indexName) {
      hasErrors = true;
      errors.indexName = errorTexts.indexName;
    }
    if (!indexCode) {
      hasErrors = true;
      errors.code = errorTexts.code;
    }
    if (!indexCountry) {
      hasErrors = true;
      errors.country = errorTexts.country;
    }
    if (!indexCurrency) {
      hasErrors = true;
      errors.currency = errorTexts.currency;
    }
    if (!indexIdentifier) {
      hasErrors = true;
      errors.identifier = errorTexts.identifier;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      const codeExists = sameIndexCodeExists(indexCode as string, indexInput?.id);
      if (codeExists) {
        setFormErrors({
          ...formErrors,
          code: codeExists ? errorTexts.existingCode : null
        });
      } else {
        handleSave(indexInput === null ? 'create' : 'update');
      }
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createIndex({
        variables: {
          input: {
            code: indexCode as string,
            currency: Number(indexCurrency?.id),
            identifier: indexIdentifier as string,
            is_online: isOnline,
            name: indexName as string,
            net_code: indexNetCode,
            country: Number(indexCountry?.id)
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IIndex);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Index created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateIndex({
        variables: {
          id: indexInput?.id as string,
          input: {
            code: indexCode as string,
            currency: Number(indexCurrency?.id),
            identifier: indexIdentifier as string,
            is_online: isOnline,
            name: indexName as string,
            net_code: indexNetCode,
            country: Number(indexCountry?.id)
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IIndex);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Index Updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  const handleIndexNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value) {
      setFormErrors({ ...formErrors, indexName: null });
    } else {
      setFormErrors({ ...formErrors, indexName: errorTexts.indexName });
    }
    setIndexName(value);
  };

  const handleCountryChange = (_: unknown, newValue: React.SetStateAction<ICountry | null>) => {
    if (newValue) {
      setFormErrors({ ...formErrors, country: null });
    } else {
      setFormErrors({ ...formErrors, country: errorTexts.country });
    }

    setIndexCountry(newValue);
  };

  const handleCurrencyChange = (_: unknown, newValue: ICurrency | null) => {
    setFormErrors({ ...formErrors, currency: newValue === null ? errorTexts.currency : null });
    setIndexCurrency(newValue);
  };

  const handleIndexNameIdentifierChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value) {
      setFormErrors({ ...formErrors, identifier: null });
    } else {
      setFormErrors({ ...formErrors, identifier: errorTexts.identifier });
    }
    setIndexIdentifier(value);
  };

  const handleIndexCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value) {
      setFormErrors({ ...formErrors, code: null });
    } else {
      setFormErrors({ ...formErrors, code: errorTexts.code });
    }
    setIndexCode(value);
  };

  const handleIndexNetCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    setIndexNetCode(value);
  };

  return (
    <DialogBox id="new-index" width={689} title="Create Index" onClose={handleCancel}>
      {indexInput && (
        <Box sx={{ textAlign: 'right', width: '100%', marginTop: '28px' }}>
          <EditButton
            id="index-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '40px 22px',
          padding: '42px 42px'
        }}
      >
        <InputComponent
          id="index-form-index-name"
          label="Index Name"
          value={indexName || ''}
          onChange={handleIndexNameChange}
          errorMsg={formErrors.indexName}
          disabled={!editable || createIndexResult.loading || updateIndexResult.loading}
          additionalStyle={{ gridColumn: 'span 4' }}
        />
        <AutocompleteComponent
          id="index-country-autocomplete"
          label="Country"
          options={(indexCountries.data?.result as never[]) || []}
          disabled={createIndexResult.loading || !editable || updateIndexResult.loading}
          values={indexCountry}
          onChange={handleCountryChange}
          getOptionLabel={(option: ICountry) => option?.name}
          errorMsg={formErrors.country}
          additionalStyle={{ gridColumn: 'span 3' }}
        />
        <AutocompleteComponent
          id="index-currency-autocomplete"
          label="Currency"
          options={(indexCurrencies.data?.result as never[]) || []}
          disabled={!editable || createIndexResult.loading || updateIndexResult.loading}
          values={indexCurrency}
          onChange={handleCurrencyChange}
          getOptionLabel={(option: ICurrency) => option.code}
          errorMsg={formErrors.currency}
          additionalStyle={{ gridColumn: 'span 1' }}
        />
        <InputComponent
          id="index-form-index-name-identifier"
          label="Index Name Identifier"
          value={indexIdentifier || ''}
          onChange={handleIndexNameIdentifierChange}
          errorMsg={formErrors.identifier}
          disabled={!editable || createIndexResult.loading || updateIndexResult.loading}
          additionalStyle={{ gridColumn: 'span 4' }}
        />
        <InputComponent
          id="index-form-index-code"
          label="Index code"
          value={indexCode || ''}
          onChange={handleIndexCodeChange}
          errorMsg={formErrors.code}
          disabled={!editable || createIndexResult.loading || updateIndexResult.loading}
          additionalStyle={{ gridColumn: 'span 2' }}
        />
        <InputComponent
          id="index-form-index-net-code"
          label="Index NET code"
          value={indexNetCode || ''}
          onChange={handleIndexNetCodeChange}
          disabled={!editable || createIndexResult.loading || updateIndexResult.loading}
          additionalStyle={{ gridColumn: 'span 2' }}
        />
        <Box sx={{ gridColumn: 'span 1' }}>
          <FormControlLabel
            control={
              <Switch
                id="is_online"
                // disableRipple
                // classes={{
                //   root: theme.MuiSwitch,
                //   switchBase: classes.switchBase,
                //   thumb: classes.thumb,
                //   track: classes.track,
                //   checked: classes.checked
                // }}
                sx={{}}
                checked={isOnline}
                onChange={() => setIsOnline(!isOnline)}
                name="checkOnline"
              />
            }
            label="Online"
            labelPlacement="start"
            style={{ color: editable ? Colors.main : Colors.dark_gray }}
            disabled={!editable || createIndexResult.loading || updateIndexResult.loading}
          />
        </Box>
      </Box>
      <Box
        className="centralize"
        sx={{ gridColumn: 'span 3', marginTop: '100px', paddingBottom: '45px' }}
      >
        <CancelButton
          id="cancel-index-button"
          disabled={createIndexResult.loading || updateIndexResult.loading}
          onClick={handleCancel}
          text={indexInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-index-button"
          disabled={!editable || createIndexResult.loading || updateIndexResult.loading}
          onClick={handleSubmit}
          text={indexInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};
export default IndexForm;
